import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDraggable } from '@dnd-kit/core';
import { CheckCircle } from 'lucide-react';
import { workOrderPriorityLabels } from '../../../types/workOrders';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import { CalendarEntry, isDraggable } from '../utils/calendarEntries';
import { getAssignmentNames } from './TaskCard';

interface CalendarChipProps {
    entry: CalendarEntry;
    onOpen: (entry: CalendarEntry) => void;
    getUserName: (uid: string) => string;
    /** Hover details are skipped on touch devices and while something is dragged. */
    showTooltip: boolean;
    /** Second line with the building (side list and phone agenda, where there is room). */
    showBuilding?: boolean;
    /** Phones read and tap only. */
    dragDisabled?: boolean;
}

const KIND_LABEL: Record<CalendarEntry['kind'], string> = {
    open: 'Ärende',
    inspection: 'Kontroll',
    completed: 'Klart ärende',
};

/** A work order that is a project delmoment: purple, like its board badge. */
export const isDelmoment = (entry: CalendarEntry): boolean => entry.kind === 'open' && !!entry.task.projectTitle;

/** "Ärende", "Delmoment", "Kontroll" or "Klart ärende" */
export function entryTypeLabel(entry: CalendarEntry): string {
    if (isDelmoment(entry)) return 'Delmoment';
    return KIND_LABEL[entry.kind];
}

/** Everything the chip leaves out: shown on hover. */
const ChipDetails: React.FC<{ entry: CalendarEntry; getUserName: (uid: string) => string }> = ({ entry, getUserName }) => {
    const { task } = entry;
    const wo = task.workOrder;
    const assigned = getAssignmentNames(task, getUserName);
    return (
        <div className="calendar-chip-details">
            <strong>{task.title}</strong>
            <div>{task.buildingName}</div>
            <div>
                {entryTypeLabel(entry)}
                {task.priority && ` · ${workOrderPriorityLabels[task.priority]} prioritet`}
            </div>
            {task.projectTitle && <div>Projekt: {task.projectTitle}</div>}
            {entry.kind === 'completed' ? (
                <div>
                    Slutförd {formatDateForDisplay(wo?.completedAt)}
                    {wo?.completedBy && ` av ${getUserName(wo.completedBy)}`}
                </div>
            ) : (
                <div>
                    Förfaller {formatDateForDisplay(task.dueDate)}
                    {entry.overdue && ' · Försenad'}
                </div>
            )}
            {assigned && <div>Tilldelad: {assigned}</div>}
        </div>
    );
};

/**
 * One calendar item: a coloured edge for its kind and the title on one line.
 * Open work orders can be dragged to another day.
 */
const CalendarChip: React.FC<CalendarChipProps> = ({ entry, onOpen, getUserName, showTooltip, showBuilding, dragDisabled }) => {
    const draggable = isDraggable(entry) && !dragDisabled;
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: entry.id,
        data: { entry },
        disabled: !draggable,
    });

    const className = [
        'calendar-chip',
        `calendar-chip--${entry.kind}`,
        isDelmoment(entry) ? 'calendar-chip--delmoment' : '',
        entry.overdue ? 'calendar-chip--overdue' : '',
        draggable ? 'calendar-chip--draggable' : '',
        isDragging ? 'calendar-chip--dragging' : '',
        showBuilding ? 'calendar-chip--with-building' : '',
    ].filter(Boolean).join(' ');

    const chip = (
        <button
            ref={setNodeRef}
            type="button"
            className={className}
            {...(draggable ? listeners : {})}
            {...attributes}
            // dnd-kit's role="button" is redundant on a <button>; keep the native one
            role={undefined}
            aria-roledescription={draggable ? 'flyttbart ärende' : undefined}
            aria-label={`${entryTypeLabel(entry)}: ${entry.task.title}, ${entry.task.buildingName}${entry.overdue ? ', försenad' : ''}`}
            onClick={() => onOpen(entry)}
        >
            {entry.kind === 'completed' && <CheckCircle size={12} aria-hidden="true" className="calendar-chip__check" />}
            <span className="calendar-chip__text">
                <span className="calendar-chip__title">{entry.task.title}</span>
                {showBuilding && <span className="calendar-chip__building">{entry.task.buildingName}</span>}
            </span>
        </button>
    );

    // Always wrapped, so a drag never remounts the chip; touch and dragging just turn the trigger off
    return (
        <OverlayTrigger
            trigger={showTooltip ? ['hover', 'focus'] : []}
            placement="auto"
            delay={{ show: 250, hide: 0 }}
            // A render function, not an element: only then does react-bootstrap pass `show`,
            // which keeps the tooltip invisible until it is positioned (no flash in the corner)
            overlay={props => (
                <Tooltip {...props} id={`calendar-chip-${entry.id}`} className="calendar-chip-tooltip">
                    <ChipDetails entry={entry} getUserName={getUserName} />
                </Tooltip>
            )}
        >
            {chip}
        </OverlayTrigger>
    );
};

/** The chip as it follows the pointer while dragged (no drag hooks, no tooltip). */
export const CalendarChipPreview: React.FC<{ entry: CalendarEntry }> = ({ entry }) => (
    <div className={`calendar-chip calendar-chip--${entry.kind} calendar-chip--preview${isDelmoment(entry) ? ' calendar-chip--delmoment' : ''}${entry.overdue ? ' calendar-chip--overdue' : ''}`}>
        <span className="calendar-chip__text">
            <span className="calendar-chip__title">{entry.task.title}</span>
        </span>
    </div>
);

export default CalendarChip;
